import type { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"

import { SUGGESTIONS_BATCH_MAX_SIZE } from "./constants"
import { ReviewSortKey, ReviewSortOrder } from "./types"
import type {
    GoogleReview,
    GoogleReviewReplyInput,
    GoogleReviewReplySuggestionResult,
    RatingFilterValue,
} from "./types"

export type ResponseStatusOption = "responded" | "notResponded"

export const hasReply = (review: GoogleReview): boolean => !!review.reviewReply?.comment

export const getSuggestibleReviews = (
    reviews: GoogleReview[],
    suggestions: Record<string, GoogleReviewReplySuggestionResult>
): GoogleReview[] =>
    reviews.filter(review => !hasReply(review) && !suggestions[review.reviewId]).slice(0, SUGGESTIONS_BATCH_MAX_SIZE)

export const toSuggestionInputs = (
    reviews: GoogleReview[],
    businessName: Maybe<string>,
    drafts: Record<string, string>
): GoogleReviewReplyInput[] =>
    reviews.map(review => ({
        reviewId: review.reviewId,
        ratingValue: review.ratingValue,
        comment: review.comment,
        reviewerDisplayName: review.reviewer.displayName,
        existingReply: review.reviewReply?.comment ?? null,
        previousSuggestion: drafts[review.reviewId] ?? null,
        businessName: businessName,
    }))

export const filterGoogleReviews = (
    reviews: GoogleReview[],
    ratingFilter: RatingFilterValue,
    responseStatusFilters: ResponseStatusOption[],
    search: string
): GoogleReview[] => {
    let filtered = reviews

    if (responseStatusFilters.length > 0 && responseStatusFilters.length < 2) {
        const shouldIncludeResponded = responseStatusFilters.includes("responded")
        filtered = filtered.filter(review => (shouldIncludeResponded ? hasReply(review) : !hasReply(review)))
    }

    if (ratingFilter !== null) {
        filtered = filtered.filter(review => review.ratingValue === ratingFilter)
    }

    if (search.trim()) {
        const searchLower = search.toLowerCase().trim()
        filtered = filtered.filter(review => {
            const authorMatch = review.reviewer?.displayName?.toLowerCase().includes(searchLower) ?? false
            const commentMatch = review.comment?.toLowerCase().includes(searchLower) ?? false
            const responseMatch = review.reviewReply?.comment?.toLowerCase().includes(searchLower) ?? false
            return authorMatch || commentMatch || responseMatch
        })
    }

    return filtered
}

export const sortGoogleReviews = (
    reviews: GoogleReview[],
    sortBy: ReviewSortKey | undefined,
    sortOrder: ReviewSortOrder | undefined
): GoogleReview[] => {
    if (!sortBy || !sortOrder) {
        return reviews
    }

    const direction = sortOrder === ReviewSortOrder.Ascend ? 1 : -1

    const sorted = [...reviews].sort((a, b) => {
        switch (sortBy) {
            case ReviewSortKey.CreateTime:
                return (new Date(a.createTime).getTime() - new Date(b.createTime).getTime()) * direction
            case ReviewSortKey.Reviewer:
                return direction * (a.reviewer?.displayName ?? "").localeCompare(b.reviewer?.displayName ?? "")
            case ReviewSortKey.RatingValue:
                return (a.ratingValue - b.ratingValue) * direction
            default:
                return 0
        }
    })

    return sorted
}
